import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Package, Truck, Plus, Save, Trash2, AlertTriangle, Edit2, X } from 'lucide-react';
import { safeConfirm } from '../../lib/confirm';

export default function AdminInventory() {
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [low, setLow] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [editVendor, setEditVendor] = useState(null);

  const loadProducts = () => api.get('/inventory/products').then(r => setProducts(r.data));
  const loadVendors = () => api.get('/inventory/vendors').then(r => setVendors(r.data));
  const loadLow = () => api.get('/inventory/low-stock').then(r => setLow(r.data));

  useEffect(() => { loadProducts(); loadVendors(); loadLow(); }, []);

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CLINIC OPERATIONS</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Inventory</h1>
        <p className="text-fg-muted mt-2">Track equipment, supplies, modalities and vendor contacts.</p>
      </div>

      {low.length > 0 && (
        <div className="mb-6 surface border border-warn rounded-lg p-4 flex items-center gap-3" data-testid="low-stock-banner">
          <AlertTriangle className="w-5 h-5 text-warn"/>
          <div className="text-sm">
            <strong className="text-warn">Low stock:</strong> {low.length} product{low.length === 1 ? '' : 's'} at or below reorder level.
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 print:hidden">
        <button onClick={()=>setTab('products')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${tab==='products' ? 'bg-brand text-white' : 'bg-[var(--bg-tertiary)] text-fg-muted'}`}
                data-testid="tab-products">
          <Package className="w-4 h-4 inline mr-1"/> Products ({products.length})
        </button>
        <button onClick={()=>setTab('vendors')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${tab==='vendors' ? 'bg-brand text-white' : 'bg-[var(--bg-tertiary)] text-fg-muted'}`}
                data-testid="tab-vendors">
          <Truck className="w-4 h-4 inline mr-1"/> Vendors ({vendors.length})
        </button>
      </div>

      {tab === 'products' && (
        <div className="admin-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold">Products & supplies</h3>
            <button onClick={()=>setEditProduct({})} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="new-product-btn">
              <Plus className="w-4 h-4"/> New product
            </button>
          </div>
          <table>
            <thead>
              <tr><th>Name</th><th>SKU</th><th>Category</th><th>Qty</th><th>Reorder ≤</th><th>Cost</th><th>Vendor</th><th></th></tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                  <td className="font-medium">{p.name}</td>
                  <td className="text-xs font-mono text-fg-muted">{p.sku || '—'}</td>
                  <td className="text-xs">{p.category || '—'}</td>
                  <td className={p.quantity <= p.reorder_level ? 'text-warn font-bold' : ''}>{p.quantity}</td>
                  <td className="text-xs">{p.reorder_level}</td>
                  <td className="text-xs">{p.cost?.toFixed?.(2) || p.cost} {p.currency}</td>
                  <td className="text-xs">{p.vendor_name || '—'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={()=>setEditProduct(p)} className="text-brand" data-testid={`edit-product-${p.product_id}`}><Edit2 className="w-3.5 h-3.5"/></button>
                      <button onClick={async ()=>{
                        if (!await safeConfirm(`Delete ${p.name}?`)) return;
                        await api.delete(`/inventory/products/${p.product_id}`);
                        loadProducts(); loadLow();
                      }} className="text-[#B83A3A]" data-testid={`delete-product-${p.product_id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!products.length && <tr><td colSpan="8" className="text-center text-fg-muted py-8">No products yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'vendors' && (
        <div className="admin-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold">Vendor directory</h3>
            <button onClick={()=>setEditVendor({})} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="new-vendor-btn">
              <Plus className="w-4 h-4"/> New vendor
            </button>
          </div>
          <table>
            <thead>
              <tr><th>Name</th><th>Contact</th><th>Email</th><th>Phone</th><th>Website</th><th></th></tr>
            </thead>
            <tbody>
              {vendors.map(v => (
                <tr key={v.vendor_id} data-testid={`vendor-row-${v.vendor_id}`}>
                  <td className="font-medium">{v.name}</td>
                  <td className="text-xs">{v.contact_name || '—'}</td>
                  <td className="text-xs">{v.email || '—'}</td>
                  <td className="text-xs">{v.phone || '—'}</td>
                  <td className="text-xs">{v.website ? <a href={v.website} target="_blank" rel="noopener noreferrer" className="text-brand">visit</a> : '—'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={()=>setEditVendor(v)} className="text-brand" data-testid={`edit-vendor-${v.vendor_id}`}><Edit2 className="w-3.5 h-3.5"/></button>
                      <button onClick={async ()=>{
                        if (!await safeConfirm(`Delete vendor ${v.name}?`)) return;
                        await api.delete(`/inventory/vendors/${v.vendor_id}`);
                        loadVendors();
                      }} className="text-[#B83A3A]" data-testid={`delete-vendor-${v.vendor_id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!vendors.length && <tr><td colSpan="6" className="text-center text-fg-muted py-8">No vendors yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {editProduct && (
        <ProductModal product={editProduct} vendors={vendors} onClose={()=>setEditProduct(null)}
                      onSaved={() => { loadProducts(); loadLow(); setEditProduct(null); }}/>
      )}
      {editVendor && (
        <VendorModal vendor={editVendor} onClose={()=>setEditVendor(null)}
                     onSaved={() => { loadVendors(); setEditVendor(null); }}/>
      )}
    </div>
  );
}

function ProductModal({ product, vendors, onClose, onSaved }) {
  const isNew = !product.product_id;
  const [form, setForm] = useState({
    name: '', sku: '', description: '', category: '', cost: 0, currency: 'PLN',
    quantity: 0, reorder_level: 0, vendor_id: '', notes: '',
    ...product,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        cost: parseFloat(form.cost) || 0,
        quantity: parseInt(form.quantity) || 0,
        reorder_level: parseInt(form.reorder_level) || 0,
        vendor_id: form.vendor_id || null,
      };
      if (isNew) await api.post('/inventory/products', body);
      else await api.put(`/inventory/products/${product.product_id}`, body);
      onSaved();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  return (
    <Modal title={isNew ? 'New product' : `Edit · ${product.name}`} onClose={onClose} testid="product-modal">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Name *"><input className="input" value={form.name} onChange={e=>set('name', e.target.value)} data-testid="p-name"/></Field>
        <Field label="SKU"><input className="input" value={form.sku||''} onChange={e=>set('sku', e.target.value)} data-testid="p-sku"/></Field>
        <Field label="Category"><input className="input" value={form.category||''} onChange={e=>set('category', e.target.value)} placeholder="Modality, supplies, equipment…" data-testid="p-cat"/></Field>
        <Field label="Cost"><input className="input" type="number" step="0.01" value={form.cost} onChange={e=>set('cost', e.target.value)} data-testid="p-cost"/></Field>
        <Field label="Currency">
          <select className="input" value={form.currency} onChange={e=>set('currency', e.target.value)}>
            <option>PLN</option><option>EUR</option><option>USD</option><option>CAD</option><option>GBP</option>
          </select>
        </Field>
        <Field label="Quantity on hand"><input className="input" type="number" value={form.quantity} onChange={e=>set('quantity', e.target.value)} data-testid="p-qty"/></Field>
        <Field label="Reorder ≤ (low-stock threshold)"><input className="input" type="number" value={form.reorder_level} onChange={e=>set('reorder_level', e.target.value)} data-testid="p-reorder"/></Field>
        <Field label="Vendor">
          <select className="input" value={form.vendor_id || ''} onChange={e=>set('vendor_id', e.target.value)} data-testid="p-vendor">
            <option value="">— none —</option>
            {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
          </select>
        </Field>
        <Field label="Description" full><textarea className="input min-h-[60px]" value={form.description||''} onChange={e=>set('description', e.target.value)}/></Field>
        <Field label="Notes" full><textarea className="input min-h-[60px]" value={form.notes||''} onChange={e=>set('notes', e.target.value)}/></Field>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        <button onClick={save} disabled={saving || !form.name} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="p-save">
          <Save className="w-4 h-4"/> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

function VendorModal({ vendor, onClose, onSaved }) {
  const isNew = !vendor.vendor_id;
  const [form, setForm] = useState({
    name: '', contact_name: '', email: '', phone: '', website: '', address: '', notes: '',
    ...vendor,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post('/inventory/vendors', form);
      else await api.put(`/inventory/vendors/${vendor.vendor_id}`, form);
      onSaved();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  return (
    <Modal title={isNew ? 'New vendor' : `Edit · ${vendor.name}`} onClose={onClose} testid="vendor-modal">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Vendor name *"><input className="input" value={form.name} onChange={e=>set('name', e.target.value)} data-testid="v-name"/></Field>
        <Field label="Contact name"><input className="input" value={form.contact_name||''} onChange={e=>set('contact_name', e.target.value)} data-testid="v-contact"/></Field>
        <Field label="Email"><input className="input" type="email" value={form.email||''} onChange={e=>set('email', e.target.value)} data-testid="v-email"/></Field>
        <Field label="Phone"><input className="input" value={form.phone||''} onChange={e=>set('phone', e.target.value)} data-testid="v-phone"/></Field>
        <Field label="Website"><input className="input" value={form.website||''} onChange={e=>set('website', e.target.value)} placeholder="https://…" data-testid="v-site"/></Field>
        <Field label="Address" full><input className="input" value={form.address||''} onChange={e=>set('address', e.target.value)} data-testid="v-addr"/></Field>
        <Field label="Notes" full><textarea className="input min-h-[60px]" value={form.notes||''} onChange={e=>set('notes', e.target.value)}/></Field>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        <button onClick={save} disabled={saving || !form.name} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="v-save">
          <Save className="w-4 h-4"/> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid={testid}>
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-soft">
          <h2 className="font-heading font-semibold">{title}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={full ? 'md:col-span-2 block' : 'block'}>
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}
